// Form Manager store — tree state + auto-save + save state matrix (§6.1).
// Orta panel edit'leri debounce ile backend'e uçurulur. Optimistic lock 412'yi
// store yakalar; UI conflict modal açar.

import { create } from "zustand";
import type {
  FrameworkVersionTreeResponse,
  PeriodType,
  Question,
  QuestionOptions,
  QuestionType,
  Section,
  SectionWithQuestions,
} from "@mahir/shared";
import { defaultOptionsForType } from "@mahir/shared";
import { ApiError } from "../api/client";
import * as API from "../api/frameworks";

export type SaveState = "idle" | "typing" | "saving" | "saved" | "conflict" | "error";

type Selection =
  | { kind: "none" }
  | { kind: "section"; id: string }
  | { kind: "question"; id: string };

interface FormManagerState {
  frameworkId: string | null;
  versionId: string | null;
  versionUpdatedAt: string | null;
  versionStatus: "draft" | "published" | "archived" | null;
  // M-L2: framework-level prompt optimistic lock — frameworks.updatedAt (version'dan bağımsız).
  frameworkUpdatedAt: string | null;
  tree: FrameworkVersionTreeResponse | null;
  loading: boolean;
  selection: Selection;
  saveState: SaveState;
  saveMessage: string | null;

  load: (frameworkId: string, versionId: string) => Promise<void>;
  select: (sel: Selection) => void;
  patchFrameworkPrompt: (frameworkPrompt: string) => Promise<void>;

  addSection: (code: string, label: string) => Promise<Section | null>;
  patchSection: (
    id: string,
    patch: {
      label?: string;
      code?: string;
      description?: string | null;
      sectionPrompt?: string;
      // Raporlama dönemi (Task 10): bölüm TAVANI + geçmiş rapor başlık eş anlamlıları.
      applicablePeriods?: PeriodType[];
      mappingHints?: string[];
    },
  ) => Promise<void>;
  removeSection: (id: string) => Promise<void>;
  moveSection: (id: string, direction: "up" | "down") => Promise<void>;

  addQuestion: (
    sectionId: string,
    code: string,
    label: string,
    type: QuestionType,
  ) => Promise<Question | null>;
  patchQuestion: (id: string, patch: Partial<QuestionPatch>) => Promise<void>;
  removeQuestion: (id: string) => Promise<void>;
  moveQuestion: (id: string, direction: "up" | "down") => Promise<void>;
  reorderQuestions: (sectionId: string, orderedIds: string[]) => Promise<void>;
  reorderSections: (orderedIds: string[]) => Promise<void>;

  changeQuestionType: (id: string, type: QuestionType) => Promise<void>;

  // --- Raporlama dönemi (Task 10) ---
  // Framework-level çeyreklik anahtarı (versiyon kilidi YOK — L2 prompt gibi canlı).
  setSupportsQuarterly: (value: boolean) => Promise<void>;
  // "Bölüm kodları standart referansıdır" — frameworks.sectionCodesAreStandardRefs (0062).
  setSectionCodesAreStandardRefs: (value: boolean) => Promise<void>;
  // Çip yazımları: optimistic + alan başına 400 ms trailing-merge → TEK PATCH.
  // Senkron (void) — bekleyen yazım `serialized()` kuyruğuna kendi zamanında girer.
  setSectionPeriods: (id: string, periods: PeriodType[]) => void;
  setQuestionPeriods: (id: string, periods: PeriodType[]) => void;
  // mappingHints: debounce yok ama optimistic — ardışık silmeler bayat listeden hesaplanmasın.
  setMappingHints: (id: string, hints: string[]) => void;
}

export interface QuestionPatch {
  label: string;
  code: string;
  description: string | null;
  helperText: string | null;
  options: QuestionOptions;
  required: boolean;
  enabled: boolean;
  priorYearHintDisabled: boolean;
  allowEvidence: boolean;
  allowNote: boolean;
  aiContext: string | null;
  // Raporlama dönemi (Task 10): soruda TERCİH; etkin dönem = bölüm ∩ soru.
  applicablePeriods: PeriodType[];
}

// Mutasyon serileştirme (Finar çift-tık bug'ı, 2026-09-10): her mutasyon
// versionUpdatedAt'i ÇAĞRI ANINDA okur. Üst üste gelen iki çağrı (çift tık "Ekle",
// debounce PATCH + "Ekle", vb.) aynı bayat token'ı taşır → backend 412 → sahte
// "Çakışma" modalı. Bu sarmalayıcı mutasyonları tek kuyrukta sıraya sokar: ikinci
// çağrı birincinin dönüşünü (ve set edilen taze token'ı) bekler. Kuyruk hata durumunda
// da ilerler (reddedilen promise zinciri kilitlemez). load()/select() kuyruk dışıdır;
// moveSection/moveQuestion sarılmaz — reorder* üzerinden kuyruğa girerler (iç içe
// sarım kendi kendini beklerdi).
let mutationChain: Promise<unknown> = Promise.resolve();
function serialized<A extends unknown[], R>(
  fn: (...args: A) => Promise<R>,
): (...args: A) => Promise<R> {
  return (...args: A): Promise<R> => {
    const run = mutationChain.then(
      () => fn(...args),
      () => fn(...args),
    );
    mutationChain = run.then(
      () => undefined,
      () => undefined,
    );
    return run;
  };
}

export const useFormManagerStore = create<FormManagerState>((set, get) => ({
  frameworkId: null,
  versionId: null,
  versionUpdatedAt: null,
  versionStatus: null,
  frameworkUpdatedAt: null,
  tree: null,
  loading: false,
  selection: { kind: "none" },
  saveState: "idle",
  saveMessage: null,

  async load(frameworkId, versionId) {
    // Bekleyen çip yazımlarını iptal et — yeni sürümün token'ıyla eski id'ye PATCH
    // gitmesi sahte 412 üretiyordu (fix round 1 / bulgu 2).
    cancelPendingPeriodWrites();
    set({ loading: true, saveState: "idle", saveMessage: null });
    try {
      const tree = await API.getVersionTree(frameworkId, versionId);
      set({
        frameworkId,
        versionId,
        versionUpdatedAt: tree.version.updatedAt,
        versionStatus: tree.version.status,
        frameworkUpdatedAt: tree.frameworkUpdatedAt,
        tree,
        loading: false,
      });
    } catch (err) {
      set({
        loading: false,
        saveState: "error",
        saveMessage: err instanceof Error ? err.message : "Yüklenemedi",
      });
    }
  },

  select(sel) {
    set({ selection: sel });
  },

  patchFrameworkPrompt: serialized(async (frameworkPrompt) => {
    const { tree, frameworkUpdatedAt } = get();
    if (!tree || !frameworkUpdatedAt) return;
    set({ saveState: "saving" });
    try {
      const res = await API.patchFrameworkPrompt(tree.version.frameworkId, {
        frameworkPrompt,
        expectedUpdatedAt: frameworkUpdatedAt,
      });
      set((s) => ({
        frameworkUpdatedAt: res.framework.updatedAt,
        tree: s.tree ? { ...s.tree, frameworkPrompt: res.framework.frameworkPrompt } : s.tree,
        saveState: "saved",
        saveMessage: "Kaydedildi",
      }));
    } catch (err) {
      applyError(set, err);
    }
  }),

  addSection: serialized(async (code, label) => {
    const { frameworkId, versionId, versionUpdatedAt } = get();
    if (!frameworkId || !versionId || !versionUpdatedAt) return null;
    set({ saveState: "saving", saveMessage: null });
    try {
      const res = await API.createSection(frameworkId, versionId, {
        expectedVersionUpdatedAt: versionUpdatedAt,
        code,
        label,
      });
      set({ versionUpdatedAt: res.newVersionUpdatedAt });
      await get().load(frameworkId, versionId);
      get().select({ kind: "section", id: res.section.id });
      set({ saveState: "saved", saveMessage: "Bölüm eklendi" });
      return res.section;
    } catch (err) {
      applyError(set, err);
      return null;
    }
  }),

  patchSection: serialized(async (id, patch) => {
    const { versionUpdatedAt } = get();
    if (!versionUpdatedAt) return;
    set({ saveState: "saving" });
    try {
      const res = await API.updateSection(id, {
        expectedVersionUpdatedAt: versionUpdatedAt,
        ...patch,
      });
      set((s) => ({
        versionUpdatedAt: res.newVersionUpdatedAt,
        tree: s.tree ? replaceSection(s.tree, id, res.section) : s.tree,
        saveState: "saved",
        saveMessage: "Kaydedildi",
      }));
    } catch (err) {
      applyError(set, err);
    }
  }),

  removeSection: serialized(async (id) => {
    const { frameworkId, versionId, versionUpdatedAt } = get();
    if (!frameworkId || !versionId || !versionUpdatedAt) return;
    set({ saveState: "saving" });
    try {
      const res = await API.deleteSection(id, versionUpdatedAt);
      set({ versionUpdatedAt: res.newVersionUpdatedAt });
      await get().load(frameworkId, versionId);
      get().select({ kind: "none" });
      set({ saveState: "saved", saveMessage: "Bölüm silindi" });
    } catch (err) {
      applyError(set, err);
    }
  }),

  async moveSection(id, direction) {
    const { tree } = get();
    if (!tree) return;
    const idx = tree.sections.findIndex((s) => s.id === id);
    if (idx < 0) return;
    const target = direction === "up" ? idx - 1 : idx + 1;
    if (target < 0 || target >= tree.sections.length) return;
    const reordered = [...tree.sections];
    [reordered[idx], reordered[target]] = [reordered[target]!, reordered[idx]!];
    await get().reorderSections(reordered.map((s) => s.id));
  },

  reorderSections: serialized(async (orderedIds) => {
    // Her bölüm için sıralı order değerlerini PATCH et. Sequential (parallel
    // değil) — framework_version updated_at her patch sonrası güncellenir.
    let uat = get().versionUpdatedAt;
    if (!uat) return;
    set({ saveState: "saving", saveMessage: null });
    try {
      for (let i = 0; i < orderedIds.length; i++) {
        const res = await API.updateSection(orderedIds[i]!, {
          expectedVersionUpdatedAt: uat,
          order: i,
        });
        uat = res.newVersionUpdatedAt;
      }
      const { frameworkId, versionId } = get();
      if (frameworkId && versionId) await get().load(frameworkId, versionId);
      set({ saveState: "saved", saveMessage: "Sıralama güncellendi" });
    } catch (err) {
      applyError(set, err);
    }
  }),

  addQuestion: serialized(async (sectionId, code, label, type) => {
    const { versionUpdatedAt, frameworkId, versionId } = get();
    if (!versionUpdatedAt || !frameworkId || !versionId) return null;
    set({ saveState: "saving" });
    try {
      const res = await API.createQuestion(sectionId, {
        expectedVersionUpdatedAt: versionUpdatedAt,
        code,
        label,
        type,
        options: defaultOptionsForType(type),
      });
      set({ versionUpdatedAt: res.newVersionUpdatedAt });
      await get().load(frameworkId, versionId);
      get().select({ kind: "question", id: res.question.id });
      set({ saveState: "saved", saveMessage: "Soru eklendi" });
      return res.question;
    } catch (err) {
      applyError(set, err);
      return null;
    }
  }),

  patchQuestion: serialized(async (id, patch) => {
    const { versionUpdatedAt } = get();
    if (!versionUpdatedAt) return;
    set({ saveState: "saving" });
    try {
      const res = await API.updateQuestion(id, {
        expectedVersionUpdatedAt: versionUpdatedAt,
        ...patch,
      });
      set((s) => ({
        versionUpdatedAt: res.newVersionUpdatedAt,
        tree: s.tree ? replaceQuestion(s.tree, id, res.question) : s.tree,
        saveState: "saved",
        saveMessage: "Kaydedildi",
      }));
    } catch (err) {
      applyError(set, err);
    }
  }),

  removeQuestion: serialized(async (id) => {
    const { versionUpdatedAt, frameworkId, versionId } = get();
    if (!versionUpdatedAt || !frameworkId || !versionId) return;
    set({ saveState: "saving" });
    try {
      const res = await API.deleteQuestion(id, versionUpdatedAt);
      set({ versionUpdatedAt: res.newVersionUpdatedAt });
      await get().load(frameworkId, versionId);
      get().select({ kind: "none" });
      set({ saveState: "saved", saveMessage: "Soru silindi" });
    } catch (err) {
      applyError(set, err);
    }
  }),

  async moveQuestion(id, direction) {
    const { tree } = get();
    if (!tree) return;
    const section = tree.sections.find((s) => s.questions.some((q) => q.id === id));
    if (!section) return;
    const idx = section.questions.findIndex((q) => q.id === id);
    const target = direction === "up" ? idx - 1 : idx + 1;
    if (target < 0 || target >= section.questions.length) return;
    const reordered = [...section.questions];
    [reordered[idx], reordered[target]] = [reordered[target]!, reordered[idx]!];
    await get().reorderQuestions(
      section.id,
      reordered.map((q) => q.id),
    );
  },

  reorderQuestions: serialized(async (_sectionId, orderedIds) => {
    let uat = get().versionUpdatedAt;
    if (!uat) return;
    set({ saveState: "saving", saveMessage: null });
    try {
      for (let i = 0; i < orderedIds.length; i++) {
        const res = await API.updateQuestion(orderedIds[i]!, {
          expectedVersionUpdatedAt: uat,
          order: i,
        });
        uat = res.newVersionUpdatedAt;
      }
      const { frameworkId, versionId } = get();
      if (frameworkId && versionId) await get().load(frameworkId, versionId);
      set({ saveState: "saved", saveMessage: "Sıralama güncellendi" });
    } catch (err) {
      applyError(set, err);
    }
  }),

  changeQuestionType: serialized(async (id, type) => {
    const { versionUpdatedAt } = get();
    if (!versionUpdatedAt) return;
    set({ saveState: "saving" });
    try {
      const res = await API.updateQuestion(id, {
        expectedVersionUpdatedAt: versionUpdatedAt,
        type,
        options: defaultOptionsForType(type),
      });
      set((s) => ({
        versionUpdatedAt: res.newVersionUpdatedAt,
        tree: s.tree ? replaceQuestion(s.tree, id, res.question) : s.tree,
        saveState: "saved",
        saveMessage: "Tip güncellendi",
      }));
    } catch (err) {
      applyError(set, err);
    }
  }),

  // Çeyreklik anahtarı — frameworks.supportsQuarterly (versiyon kilidi kullanmaz).
  // Optimistic: switch anında döner, hata halinde eski değere geri alınır (2A).
  setSupportsQuarterly: serialized(async (value: boolean) => {
    const { tree } = get();
    if (!tree) return;
    const prev = tree.supportsQuarterly;
    set((s) => ({
      tree: s.tree ? { ...s.tree, supportsQuarterly: value } : s.tree,
      saveState: "saving",
      saveMessage: null,
    }));
    try {
      await API.updateFramework(tree.version.frameworkId, { supportsQuarterly: value });
      set({ saveState: "saved", saveMessage: "Kaydedildi" });
    } catch (err) {
      set((s) => ({ tree: s.tree ? { ...s.tree, supportsQuarterly: prev } : s.tree }));
      applyError(set, err);
    }
  }),

  // Optimistic + hata halinde geri alma (çeyreklik anahtarıyla aynı desen).
  setSectionCodesAreStandardRefs: serialized(async (value: boolean) => {
    const { tree } = get();
    if (!tree) return;
    const prev = tree.sectionCodesAreStandardRefs;
    set((s) => ({
      tree: s.tree ? { ...s.tree, sectionCodesAreStandardRefs: value } : s.tree,
      saveState: "saving",
      saveMessage: null,
    }));
    try {
      await API.updateFramework(tree.version.frameworkId, { sectionCodesAreStandardRefs: value });
      set({ saveState: "saved", saveMessage: "Kaydedildi" });
    } catch (err) {
      set((s) => ({
        tree: s.tree ? { ...s.tree, sectionCodesAreStandardRefs: prev } : s.tree,
      }));
      applyError(set, err);
    }
  }),

  setSectionPeriods(id, periods) {
    schedulePeriodWrite("section", id, periods);
  },

  setQuestionPeriods(id, periods) {
    schedulePeriodWrite("question", id, periods);
  },

  // mappingHints — debounce YOK (ekleme/silme anında), ama OPTIMISTIC:
  // aksi halde ardışık iki silme bayat prop'tan hesaplanıp ilkini geri getiriyordu
  // (fix round 1 / bulgu 1). Hata → burst öncesi listeye dönülür.
  setMappingHints(id, hints) {
    const store = useFormManagerStore;
    const section = store.getState().tree?.sections.find((s) => s.id === id);
    if (!section) return;
    const rollback = [...section.mappingHints];
    store.setState((s) => ({
      tree: s.tree ? withMappingHints(s.tree, id, hints) : s.tree,
      saveState: "typing",
      saveMessage: null,
    }));
    void commitMappingHints(id, hints, rollback);
  },
}));

// --- Dönem çipi yazımları: 400 ms trailing-merge (Task 10, karar 2A) ---
// Çip tıklaması ağacı ANINDA günceller (optimistic), ağ isteği 400 ms sessizlikten
// sonra SON değerle TEK kez gider. Kuyruk alan başına ayrıdır (`section:<id>` /
// `question:<id>`) — etiket/kod gibi diğer alanların 800 ms yolunu geciktirmez.
// Gerçek PATCH yine `serialized()` içinden geçer (bayat versionUpdatedAt yok).
const PERIOD_MERGE_MS = 400;
type PeriodTarget = "section" | "question";
const periodTimers = new Map<string, ReturnType<typeof setTimeout>>();
// Burst'ün BAŞINDAKİ değer — hata halinde buraya dönülür (ara adımlara değil).
// Silme TIMER'da değil, commit çözüldüğünde yapılır (fix round 1 / bulgu 8): istek
// uçarken gelen yeni tık kendi burst'ünü başlatır, eski hata onu ezmemeli.
const periodRollback = new Map<string, PeriodType[]>();
// Alan başına burst sayacı — commit, kendi burst'ü hâlâ güncel mi diye bakar.
const periodGeneration = new Map<string, number>();

// Versiyon/framework değişince (load) bekleyen çip yazımlarını iptal et
// (fix round 1 / bulgu 2): aksi halde eski section id + YENİ versionUpdatedAt ile
// PATCH gidip sahte 412 "Çakışma" üretiyordu.
function cancelPendingPeriodWrites(): void {
  for (const timer of periodTimers.values()) clearTimeout(timer);
  periodTimers.clear();
  periodRollback.clear();
  periodGeneration.clear();
}

function readPeriods(
  tree: FrameworkVersionTreeResponse | null,
  kind: PeriodTarget,
  id: string,
): PeriodType[] | null {
  if (!tree) return null;
  if (kind === "section") {
    const section = tree.sections.find((s) => s.id === id);
    return section ? [...section.applicablePeriods] : null;
  }
  for (const s of tree.sections) {
    const q = s.questions.find((qq) => qq.id === id);
    if (q) return [...q.applicablePeriods];
  }
  return null;
}

function withPeriods(
  tree: FrameworkVersionTreeResponse,
  kind: PeriodTarget,
  id: string,
  periods: PeriodType[],
): FrameworkVersionTreeResponse {
  if (kind === "section") {
    return {
      ...tree,
      sections: tree.sections.map((s) => (s.id === id ? { ...s, applicablePeriods: periods } : s)),
    };
  }
  return {
    ...tree,
    sections: tree.sections.map((s) => ({
      ...s,
      questions: s.questions.map((q) => (q.id === id ? { ...q, applicablePeriods: periods } : q)),
    })),
  };
}

function schedulePeriodWrite(kind: PeriodTarget, id: string, periods: PeriodType[]): void {
  const store = useFormManagerStore;
  const key = `${kind}:${id}`;
  if (!periodRollback.has(key)) {
    const current = readPeriods(store.getState().tree, kind, id);
    if (current) periodRollback.set(key, current);
  }
  periodGeneration.set(key, (periodGeneration.get(key) ?? 0) + 1);
  store.setState((s) => ({
    tree: s.tree ? withPeriods(s.tree, kind, id, periods) : s.tree,
    saveState: "typing",
    saveMessage: null,
  }));
  const pending = periodTimers.get(key);
  if (pending !== undefined) clearTimeout(pending);
  // Yazım, zamanlandığı SÜRÜME aittir — timer dolduğunda başka sürüm yüklüyse atılır.
  const versionId = store.getState().versionId;
  periodTimers.set(
    key,
    setTimeout(() => {
      periodTimers.delete(key);
      const rollback = periodRollback.get(key) ?? periods;
      void commitPeriods(kind, id, periods, rollback, versionId, periodGeneration.get(key) ?? 0);
    }, PERIOD_MERGE_MS),
  );
}

const commitPeriods = serialized(
  async (
    kind: PeriodTarget,
    id: string,
    periods: PeriodType[],
    rollback: PeriodType[],
    versionId: string | null,
    generation: number,
  ) => {
    const store = useFormManagerStore;
    const set = store.setState;
    const key = `${kind}:${id}`;
    // Kuyrukta beklerken sürüm değiştiyse yazımı at (bulgu 2) — eski id + taze token
    // kombinasyonu sahte 412 üretirdi.
    if (store.getState().versionId !== versionId) {
      if (periodGeneration.get(key) === generation) {
        periodRollback.delete(key);
        periodGeneration.delete(key);
      }
      return;
    }
    const versionUpdatedAt = store.getState().versionUpdatedAt;
    if (!versionUpdatedAt) {
      // Sessiz düşüş yok (bulgu 9): "typing" göstergede asılı kalmasın.
      set({ saveState: "error", saveMessage: "Sürüm bilgisi yok — sayfayı yenileyin" });
      return;
    }
    set({ saveState: "saving", saveMessage: null });
    try {
      if (kind === "section") {
        const res = await API.updateSection(id, {
          expectedVersionUpdatedAt: versionUpdatedAt,
          applicablePeriods: periods,
        });
        set((s) => ({
          versionUpdatedAt: res.newVersionUpdatedAt,
          tree: s.tree ? replaceSection(s.tree, id, res.section) : s.tree,
          saveState: "saved",
          saveMessage: "Kaydedildi",
        }));
      } else {
        const res = await API.updateQuestion(id, {
          expectedVersionUpdatedAt: versionUpdatedAt,
          applicablePeriods: periods,
        });
        set((s) => ({
          versionUpdatedAt: res.newVersionUpdatedAt,
          tree: s.tree ? replaceQuestion(s.tree, id, res.question) : s.tree,
          saveState: "saved",
          saveMessage: "Kaydedildi",
        }));
      }
      // Başarı: bu burst hâlâ güncelse geri-alma noktası temizlenir; arada yeni tık
      // geldiyse onun geri-alma noktası artık SUNUCUNUN onayladığı değerdir (bulgu 8).
      if (periodGeneration.get(key) === generation) {
        periodRollback.delete(key);
        periodGeneration.delete(key);
      } else {
        periodRollback.set(key, periods);
      }
    } catch (err) {
      // Optimistic yazımı geri al — 412 çakışma mesajı applyError'dan gelir.
      // İstek uçarken yeni tık geldiyse GERİ ALMA (kullanıcının son seçimini ezerdi).
      if (periodGeneration.get(key) === generation) {
        set((s) => ({ tree: s.tree ? withPeriods(s.tree, kind, id, rollback) : s.tree }));
        periodRollback.delete(key);
        periodGeneration.delete(key);
      }
      applyError(set, err);
    }
  },
);

// --- mappingHints yazımı (fix round 1 / bulgu 1): optimistic + serileştirilmiş ---
function withMappingHints(
  tree: FrameworkVersionTreeResponse,
  id: string,
  hints: string[],
): FrameworkVersionTreeResponse {
  return {
    ...tree,
    sections: tree.sections.map((s) => (s.id === id ? { ...s, mappingHints: hints } : s)),
  };
}

const commitMappingHints = serialized(async (id: string, hints: string[], rollback: string[]) => {
  const store = useFormManagerStore;
  const set = store.setState;
  const versionUpdatedAt = store.getState().versionUpdatedAt;
  if (!versionUpdatedAt) {
    set({ saveState: "error", saveMessage: "Sürüm bilgisi yok — sayfayı yenileyin" });
    return;
  }
  set({ saveState: "saving", saveMessage: null });
  try {
    const res = await API.updateSection(id, {
      expectedVersionUpdatedAt: versionUpdatedAt,
      mappingHints: hints,
    });
    set((s) => ({
      versionUpdatedAt: res.newVersionUpdatedAt,
      tree: s.tree ? replaceSection(s.tree, id, res.section) : s.tree,
      saveState: "saved",
      saveMessage: "Kaydedildi",
    }));
  } catch (err) {
    set((s) => ({ tree: s.tree ? withMappingHints(s.tree, id, rollback) : s.tree }));
    applyError(set, err);
  }
});

function replaceSection(
  tree: FrameworkVersionTreeResponse,
  id: string,
  updated: Section,
): FrameworkVersionTreeResponse {
  return {
    ...tree,
    sections: tree.sections.map((s) =>
      s.id === id ? { ...s, ...updated, questions: s.questions } : s,
    ),
  };
}

function replaceQuestion(
  tree: FrameworkVersionTreeResponse,
  id: string,
  updated: Question,
): FrameworkVersionTreeResponse {
  return {
    ...tree,
    sections: tree.sections.map((s) => ({
      ...s,
      questions: s.questions.map((q) => (q.id === id ? updated : q)),
    })),
  };
}

function applyError(set: (partial: Partial<FormManagerState>) => void, err: unknown): void {
  if (err instanceof ApiError) {
    if (err.status === 412) {
      set({
        saveState: "conflict",
        saveMessage:
          "Çerçeve sizin gördüğünüz halinden sonra güncellenmiş; son işlem uygulanmadı. Güncel hali yükleyin.",
      });
      return;
    }
    set({ saveState: "error", saveMessage: err.message });
    return;
  }
  set({
    saveState: "error",
    saveMessage: err instanceof Error ? err.message : "Kaydedilemedi",
  });
}

// Resolved selection — pure function (Zustand selector DEĞİL; component
// içinde useMemo ile çağrılır ki her render'da yeni object üretimi Zustand
// getSnapshot infinite loop'una yol açmasın).
export type ResolvedSelection =
  | { kind: "none" }
  | { kind: "section"; section: SectionWithQuestions }
  | { kind: "question"; question: Question; section: SectionWithQuestions };

export function resolveSelection(
  tree: FrameworkVersionTreeResponse | null,
  selection: Selection,
): ResolvedSelection {
  if (!tree || selection.kind === "none") return { kind: "none" };
  if (selection.kind === "section") {
    const section = tree.sections.find((s) => s.id === selection.id);
    return section ? { kind: "section", section } : { kind: "none" };
  }
  const qid = selection.id;
  for (const s of tree.sections) {
    const q = s.questions.find((qq) => qq.id === qid);
    if (q) return { kind: "question", question: q, section: s };
  }
  return { kind: "none" };
}
